import React, { useState, useEffect, useRef } from "react";
import "./CommentsBlock.css";
import dotsIcon from "../../assets/dots.svg";
import Avatar from "../Avatar/Avatar";
import { useTranslation } from "react-i18next";
import CommentContextMenu from "./CommentContextMenu";
import VideoMarker from "./VideoMarker";
import momentTz from "moment-timezone";
import emojiIcon from "../../assets/emojiLogo.svg";
import TagComponent from "../TagsSuggestionDropdown/TagComponent";
import UserPopup from "./UserPopup";
import { AudioPlayer } from "react-audio-play";
import { ReactComponent as CheckIcon } from "../../assets/blackCheck.svg";
import { createVersionComment } from "../../store/projectsSlice";
import { fetchCommentReplies } from "../../store/appSlice";
import {
  currentOrganizationSelector,
  userSelector,
} from "../../store/selectors";
import { useDispatch, useSelector } from "react-redux";
import EmojiContextMenu from "./EmojiContextMenu";
import toast from "react-hot-toast";
import {
  getOrganizationReactions,
  removeCommentReaction,
  setCommentReaction,
} from "../../store/usersSlice";
import { update } from "lodash";

const colorsArray = [
  "#9D7BFF",
  "#EA5B6F",
  "#FF78B5",
  "#C64CBF",
  "#B69BF0",
  "#F49BAB",
  "#D23D9B",
  "#CA6DC1",
  "#6B97FD",
  "#5070FF",
  "#3386FF",
  "#FFBA5F",
  "#E98D35",
  "#FD895F",
  "#FAFA92",
  "#FEFFC1",
  "#FFCD67",
  "#AFC55E",
  "#3EDD95",
  "#88D669",
  "#87DDFF",
  "#2BC1DF",
  "#17B7AC",
  "#93DA97",
];

const CommentsBlock = ({
  commentsArray = [],
  myProjectRole,
  isShared,
  isViewer,
  onArchive,
  onRestore,
  updateComments,
  isSendingComment,
  videoControls,
  videoRef,
  currentTime,
  onPressTimestamp,
  onDelete,
  onForceDelete,
  commentsFilter,
  versionId,
  highlightCommentId, // NEW PROP
  tagsDefaultColors,
  selectedTag,
  setSelectedTag,
  onSetEditedComment,
  setCurrentHoveredCommentId,
  currentHoveredCommentId,
  onShowSharedNeedLogin,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentOrganization = useSelector(currentOrganizationSelector);
  const [comments, setComments] = useState(commentsArray);
  const user = useSelector(userSelector);
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [openReplyId, setOpenReplyId] = useState(null);
  const [replies, setReplies] = useState({});
  const [replyInputs, setReplyInputs] = useState({});
  const [activeMarker, setActiveMarker] = useState(null);
  const [hoveredUser, setHoveredUser] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [currentEditedComment, setCurrentEditedComment] = useState(false);
  const [reactionsArray, setReactionsArray] = useState([]);
  const [commentsEmojies, setCommentsEmojies] = useState({});
  const [shouldNotHighlightComment, setShouldNotHighlightComment] =
    useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({
    top: 0,
    left: 0,
  });
  const [isEmojiContextMenuVisible, setIsEmojiContextMenuVisible] =
    useState(false);
  // For scroll and highlight
  const commentRefs = useRef({});
  const prevCommentsRef = useRef([]);
  const [highlightedCommentId, setHighlightedCommentId] = useState(null);

  // Scroll and highlight on external prop change as well
  useEffect(() => {
    if (highlightCommentId && commentRefs.current[highlightCommentId]) {
      commentRefs.current[highlightCommentId].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      setHighlightedCommentId(highlightCommentId);
      //setTimeout(() => setHighlightedCommentId(null), 2000);
    }
  }, [highlightCommentId]);

  useEffect(() => {
    setComments(commentsArray);
    let newReactionsComments = {};
    for (let i = 0; i < commentsArray.length; i++) {
      let commentReactions = commentsArray[i].reactions;
      const likesReactionsIndex = commentReactions.findIndex(
        (commentReaction) => {
          return commentReaction.reaction.id === 2;
        }
      );
      if (likesReactionsIndex < 0) {
        commentReactions = [
          {
            count: 0,
            reactedByCurrentUser: false,
            reaction: {
              code: "like",
              emoji: "👍",
              id: 2,
              name: "Like",
              orgId: null,
            },
          },
          ...commentReactions,
        ];
      }
      newReactionsComments = {
        ...newReactionsComments,
        [commentsArray[i].id]: commentReactions,
      };
    }
    setCommentsEmojies(newReactionsComments);
  }, [commentsArray]);

  // Scroll and highlight to the new comment when added
  useEffect(() => {
    const prev = prevCommentsRef.current;
    if (comments && prev) {
      // Find added comment
      const prevIds = prev.map((c) => c.id);
      const added = comments.find((c) => !prevIds.includes(c.id));
      if (added && commentRefs.current[added.id]) {
        // Scroll to and highlight
        commentRefs.current[added.id].scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        setHighlightedCommentId(added.id);
        // Remove highlight after 2s
        setTimeout(() => setHighlightedCommentId(null), 2000);
      }
    }
    prevCommentsRef.current = comments;
  }, [comments]);

  useEffect(() => {
    updateReplies();
  }, [comments.length]);

  useEffect(() => {
    if (isShared || !user) return;
    const userProjectRoles = user?.projectRoles || [];
    const currentRole = userProjectRoles.find((role) => {
      return role.allowedContentVersionIds.includes(+versionId);
    });
    const neededOrgId = currentRole?.organizationId || currentOrganization.id;
    dispatch(getOrganizationReactions({ organizationId: neededOrgId })).then(
      (res) => {
        if (!res.error) {
          setReactionsArray(res.payload);
        }
      }
    );
  }, [user]);

  const updateReplies = async () => {
    let newReplies = {};
    for (let i = 0; i < comments.length; i++) {
      let commentId = comments[i].id;
      const result = await dispatch(fetchCommentReplies(commentId));
      if (!result.err) {
        const commentReplies = result.payload;
        newReplies[commentId] = commentReplies;
      }
    }
    setReplies(newReplies);
  };

  // Update active marker based on current time
  useEffect(() => {
    const commentWithMarker = comments.find((comment) => {
      if (!comment.markerPoint) return false;
      const startTime = comment.timeRange?.start || 0;
      const endTime = comment.timeRange?.end || startTime;
      return currentTime >= startTime && currentTime <= endTime;
    });

    setActiveMarker(commentWithMarker?.markerPoint || null);
  }, [currentTime, comments]);

  const handleDelete = async (commentId) => {
    const res = await onDelete(commentId);
    updateReplies();
    //setComments(comments.filter((comment) => comment.id !== commentId));
  };

  const handleForceDelete = async (commentId) => {
    const res = await onForceDelete(commentId);
    updateReplies();
    //setComments(comments.filter((comment) => comment.id !== commentId));
  };

  const handleMenuClick = (e, commentId) => {
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();
    setContextMenuPosition({
      top: rect.bottom + window.scrollY,
      left: rect.right + window.scrollX - 140,
    });
    setActiveMenuId(activeMenuId === commentId ? null : commentId);
  };

  const handleEmojiMenuClick = (e, commentId) => {
    if (isShared) return;
    // if (isEmojiContextMenuVisible) {
    //   e.stopPropagation();
    //   e.preventDefault();
    //   setIsEmojiContextMenuVisible(false);
    //   return;
    // }
    e.stopPropagation();

    if (isEmojiContextMenuVisible !== commentId) {
      const rect = e.currentTarget.getBoundingClientRect();
      setContextMenuPosition({
        top: rect.bottom + window.scrollY + 10,
        left: rect.right + window.scrollX - 30,
      });
    }

    setIsEmojiContextMenuVisible(
      isEmojiContextMenuVisible === commentId ? null : commentId
    );
  };

  const onAddEmoji = () => {
    updateComments();
  };

  const handleSetReaction = (commentId, emoji) => {
    if (isShared) return;
    dispatch(
      setCommentReaction({
        commentId: commentId,
        reactionId: emoji.id,
      })
    ).then((res) => {
      if (!res.error) {
        onAddEmoji();
      }
    });
  };

  const handleRemoveReaction = (commentId) => {
    if (isShared) return;
    dispatch(
      removeCommentReaction({
        commentId: commentId,
      })
    ).then((res) => {
      if (!res.error) {
        onAddEmoji();
      }
    });
  };

  const handleAddReply = (commentId) => {
    if (!replyInputs[commentId] || !replyInputs[commentId].trim()) return;

    const commentData = {
      parentId: commentId,
      content: replyInputs[commentId].trim(),
      authorId: user.userId,
    };

    dispatch(createVersionComment({ versionId, commentData })).then((res) => {
      if (!res.error) {
        const updatedReplies = { ...replies };

        if (!updatedReplies[commentId]) {
          updatedReplies[commentId] = [];
        }

        updatedReplies[commentId].push(res.payload);
        setReplies(updatedReplies);
        setReplyInputs({
          ...replyInputs,
          [commentId]: "",
        });
      }
    });

    // setReplies((prev) => ({
    //   ...prev,
    //   [commentId]: [
    //     ...(prev[commentId] || []),
    //     {
    //       id: Date.now(),
    //       author: "Вы",
    //       time: new Date().toLocaleTimeString("ru-RU", {
    //         hour: "2-digit",
    //         minute: "2-digit",
    //       }),
    //       content: replyInput.trim(),
    //     },
    //   ],
    // }));
    // setReplyInput("");
  };
  const handleTimestampClick = (comment) => {
    if (comment.timecode.includes("-")) {
      const [timecodeStart, timecodeEnd] = comment.timecode.split(" - ");
      videoControls.seek(+timecodeStart);
    } else {
      videoControls.seek(+comment.timecode);
    }

    if (comment.markerPoint) {
      onPressTimestamp(comment.markerPoint);
      setActiveMarker(comment.markerPoint);
    }
  };

  const handleUserAvatarMouseEnter = (e, user) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPopupPosition({
      top: rect.top - 10,
      left: rect.left - 230,
    });
    setHoveredUser(user);
  };

  const handleUserAvatarMouseLeave = () => {
    setHoveredUser(null);
  };

  const renderCommentContent = (comment) => {
    const content = comment.content;
    // Split content by @ mentions
    const parts = content.split(/(@[^\s]+)/g);
    const commentText = parts.map((part, index) => {
      if (part.startsWith("@")) {
        return (
          <span key={index} className="user-mention">
            {part}
          </span>
        );
      }
      return part;
    });

    return (
      <div style={{ display: "inline-block" }}>
        {comment.timecode && (
          <>
            <span className="comment-timecode-inline">
              {formatTimecode(comment.timecode)}
            </span>
            {"  "}
          </>
        )}

        <span className="comment-text">{commentText}</span>
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const res = momentTz(dateString).tz("etc/gmt-6").format("HH:mm");
    return res;
  };

  const formatTimecode = (timecode) => {
    if (!timecode.includes("-")) {
      let numberOfMinutes = Math.floor(+timecode / 60);
      let numberOfSeconds = Math.floor(+timecode - 60 * numberOfMinutes);

      if (numberOfMinutes < 10)
        numberOfMinutes = "0" + numberOfMinutes.toString();
      if (numberOfSeconds < 10)
        numberOfSeconds = "0" + numberOfSeconds.toString();
      return `${numberOfMinutes}:${numberOfSeconds}`;
    }

    const [timeRangeStart, timeRangeEnd] = timecode.split(" - ");

    let numberOfMinutesStart = Math.floor(+timeRangeStart / 60);
    let numberOfSecondsStart = Math.floor(
      +timeRangeStart - 60 * numberOfMinutesStart
    );
    if (numberOfMinutesStart < 10)
      numberOfMinutesStart = "0" + numberOfMinutesStart.toString();
    if (numberOfSecondsStart < 10)
      numberOfSecondsStart = "0" + numberOfSecondsStart.toString();

    let numberOfMinutesEnd = Math.floor(+timeRangeEnd / 60);
    let numberOfSecondsEnd = Math.floor(
      +timeRangeEnd - 60 * numberOfMinutesEnd
    );
    if (numberOfMinutesEnd < 10)
      numberOfMinutesEnd = "0" + numberOfMinutesEnd.toString();
    if (numberOfSecondsEnd < 10)
      numberOfSecondsEnd = "0" + numberOfSecondsEnd.toString();

    return `${numberOfMinutesStart}:${numberOfSecondsStart} - ${numberOfMinutesEnd}:${numberOfSecondsEnd}`;
  };

  const handleEditComment = (comment) => {
    onSetEditedComment(comment);
  };

  return (
    <div className="comments-block">
      {comments.map((comment) => (
        <div
          key={comment.id}
          ref={(el) => {
            commentRefs.current[comment.id] = el;
          }}
          className={`comment-item ${
            commentsFilter === "archived" ? "archived" : ""
          }${
            highlightedCommentId === comment.id && !shouldNotHighlightComment
              ? " highlight"
              : ""
          } ${
            currentHoveredCommentId === comment.id && !shouldNotHighlightComment
              ? " hovered-highlight"
              : ""
          }`}
        >
          <div
            className="comment-fields"
            onClick={() => {
              handleTimestampClick(comment);
            }}
            onMouseEnter={() => setCurrentHoveredCommentId(comment.id)}
            onMouseLeave={() => setCurrentHoveredCommentId(null)}
          >
            <div className="comment-header">
              <div className="comment-author-info">
                <Avatar
                  user={comment.author}
                  name={comment.author.email}
                  size={24}
                />
                <span className="comment-author">
                  {(comment.author.firstName &&
                    `${comment.author.firstName} ${comment.author.lastName}`) ||
                    comment.author.email}
                </span>
              </div>
              <span className="comment-time">
                {formatDate(comment.createdDate)}
              </span>
            </div>

            <div className="comment-content-wrapper">
              <div className="comment-tags">
                {/* <div
                  className="comment-timestamp"
                  onClick={() => handleTimestampClick(comment)}
                  style={{ cursor: "pointer" }}
                >
                  <span className="comment-timestamp-text">
                    {formatTimecode(comment.timecode)}
                  </span>
                </div> */}
                {(comment.tags &&
                  comment.tags.length &&
                  comment.tags.map((tag) => (
                    <TagComponent
                      tagsDefaultColors={tagsDefaultColors}
                      key={tag.id}
                      tag={tag}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (!tag.color) tag.color = tagsDefaultColors[tag.id];
                        setSelectedTag(
                          selectedTag.name === "Все"
                            ? tag
                            : { name: "Все", id: -1 }
                        );
                      }}
                    />
                  ))) ||
                  null}
                {(comment.mentions && comment.mentions.length && (
                  <div>
                    <Avatar
                      user={comment.mentions[0]}
                      name={comment.mentions[0].email}
                      size={27}
                      onMouseEnter={(e) =>
                        handleUserAvatarMouseEnter(e, comment.mentions[0])
                      }
                      onMouseLeave={handleUserAvatarMouseLeave}
                    />
                  </div>
                )) ||
                  null}
                {comment.statuses &&
                  comment.statuses.map((status) => (
                    <div
                      key={status.id}
                      className="comment-status"
                      style={{ backgroundColor: status.color }}
                    >
                      <span className="comment-status-text">{status.id}</span>
                    </div>
                  ))}
              </div>

              <span className="comment-text">
                {renderCommentContent(comment)}
              </span>
            </div>

            {comment.attachment && (
              <div className="comment-attachment">
                <img
                  src={comment.attachment.icon}
                  alt="file"
                  className="attachment-icon"
                />
                <span className="attachment-name">
                  {comment.attachment.name}
                </span>
              </div>
            )}

            {comment.image && (
              <img
                src={comment.image}
                alt="attachment"
                className="comment-image"
              />
            )}
            {(comment.attachments && comment.attachments.length && (
              <div style={{ width: "100%", height: 50 }}>
                <AudioPlayer
                  className="comment-voice-message"
                  src={comment.attachments[0].cdnUrl}
                  sliderColor="rgb(154, 106, 255)"
                  style={{
                    background: "transparent",
                    fontSize: 12,
                    border: "1px solid #333b4b",
                    borderRadius: "15px",
                    height: 40,
                  }}
                />
              </div>
            )) ||
              null}
            <div className="comment-emoji-row">
              {commentsEmojies[comment.id] &&
                commentsEmojies[comment.id].map((commentReaction, index) => (
                  <div
                    className="comment-emoji-item"
                    style={{
                      backgroundColor: commentReaction.reactedByCurrentUser
                        ? "#191d248a"
                        : commentReaction.count === 0
                        ? "transparent"
                        : "#333b4b8a",
                      marginLeft:
                        index === 0
                          ? commentReaction.reactedByCurrentUser
                            ? -12
                            : -13
                          : 0,
                      paddingRight:
                        index === 0
                          ? commentReaction.reactedByCurrentUser
                            ? 10
                            : 5
                          : 10,
                      // backgroundColor: "#333b4b8a",
                      // border: commentReaction.reactedByCurrentUser
                      //   ? "1px solid rgb(154, 106, 255)"
                      //   : "1px solid #333b4b8a",
                      // border: commentReaction.reactedByCurrentUser
                      //   ? "1px solid #191d248a"
                      //   : "1px solid #333b4b8a",
                      // paddingLeft: commentReaction.count === 0 ? 1 : 13,
                      // paddingRight: commentReaction.count === 0 ? 1 : 13,
                    }}
                    onClick={() => {
                      // if (isViewer) return;
                      if (isShared) {
                        onShowSharedNeedLogin();
                        return;
                      }
                      if (commentReaction.reactedByCurrentUser) {
                        handleRemoveReaction(comment.id);
                      }
                      if (!commentReaction.reactedByCurrentUser) {
                        handleSetReaction(comment.id, commentReaction.reaction);
                      }
                    }}
                    onMouseEnter={() => setShouldNotHighlightComment(true)}
                    onMouseLeave={() => setShouldNotHighlightComment(false)}
                  >
                    <span
                      style={{ fontSize: 22 }}
                    >{`${commentReaction.reaction.emoji}`}</span>
                    {!!commentReaction.count && (
                      <span className="comment-emoji-item-count">
                        {commentReaction.count}
                      </span>
                    )}
                  </div>
                ))}
              <div
                className="comment-emoji-item-transparent"
                onClick={(e) => {
                  if (isShared) {
                    onShowSharedNeedLogin();
                    return;
                  }
                  handleEmojiMenuClick(e, comment.id);
                }}
                onMouseEnter={() => setShouldNotHighlightComment(true)}
                onMouseLeave={() => setShouldNotHighlightComment(false)}
                style={{
                  width: "auto",
                  paddingLeft: 2,
                  paddingRight: 2,
                }}
              >
                <img
                  id="emoji-icon"
                  src={emojiIcon}
                  style={{ width: 16, height: 16 }}
                />
                <EmojiContextMenu
                  emojiArray={reactionsArray}
                  onAddEmoji={onAddEmoji}
                  isOpen={isEmojiContextMenuVisible === comment.id}
                  onClose={(e) => handleEmojiMenuClick(e, comment.id)}
                  comment={comment}
                  contextMenuPosition={contextMenuPosition}
                />
              </div>
            </div>
            {(!isShared && !isViewer && (
              <div className="comment-footer">
                {!isShared && (
                  <div>
                    <span
                      className="reply-text"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (isViewer) return;
                        if (isShared) {
                          onShowSharedNeedLogin();
                          return;
                        }
                        setOpenReplyId(
                          openReplyId === comment.id ? null : comment.id
                        );
                      }}
                    >
                      {`${t("comments.reply")}`}
                    </span>
                    <span className="reply-text-count">
                      {replies[comment.id] && replies[comment.id].length
                        ? ` ${replies[comment.id].length}`
                        : ""}
                    </span>
                  </div>
                )}
                <div
                  style={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  {commentsFilter !== "archived" &&
                    !isShared &&
                    (!myProjectRole ||
                      myProjectRole.role !== "COMMENTER" ||
                      comment.author.id === user.userId) && (
                      <div
                        className="comment-solved-button"
                        onMouseEnter={() => setShouldNotHighlightComment(true)}
                        onMouseLeave={() => setShouldNotHighlightComment(false)}
                        onClick={() => {
                          onArchive(comment.id);
                          toast.success("Комментарий помечен как решенный");
                        }}
                      >
                        <CheckIcon
                          style={{
                            width: 24,
                            height: 24,
                            color: "rgb(121, 201, 121)",
                            transform: "scale(1)",
                          }}
                        />
                      </div>
                    )}
                  {(!isShared &&
                    (!myProjectRole ||
                      myProjectRole.role !== "COMMENTER" ||
                      comment.author.id === user.userId) && (
                      <>
                        <img
                          src={dotsIcon}
                          alt="menu"
                          className="menu-icon"
                          onClick={(e) => handleMenuClick(e, comment.id)}
                        />
                        <CommentContextMenu
                          comment={comment}
                          commentsFilter={commentsFilter}
                          isOpen={activeMenuId === comment.id}
                          onClose={() => setActiveMenuId(null)}
                          onArchive={() => onArchive(comment.id)}
                          onRestore={() => onRestore(comment.id)}
                          onDelete={() => handleDelete(comment.id)}
                          onForceDelete={() => handleForceDelete(comment.id)}
                          isArchived={commentsFilter === "archived"}
                          position={{ top: "100%", right: 0 }}
                          onEdit={() => handleEditComment(comment)}
                          contextMenuPosition={contextMenuPosition}
                        />
                      </>
                    )) || <div style={{ width: 5, height: 5 }} />}
                </div>
              </div>
            )) ||
              null}
          </div>
          <div className="replies-block">
            {(replies && replies[comment.id] && replies[comment.id].length && (
              <div className="replies-list">
                {(replies[comment.id] || []).map((reply) => (
                  <div className="reply-item" key={reply.id}>
                    <div className="reply-meta">
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <Avatar
                          user={reply.author}
                          size={24}
                          name={reply.author.email}
                        />
                        <span className="reply-author">
                          {reply.author.nickname || reply.author.email}
                        </span>
                      </div>
                      <span className="reply-time">
                        {formatDate(reply.createdDate)}
                      </span>
                    </div>
                    <div className="reply-content">
                      <span className="reply-text">
                        {renderCommentContent(reply)}
                      </span>
                      <div style={{ position: "relative" }}>
                        {(!isShared &&
                          !isViewer &&
                          (!myProjectRole ||
                            myProjectRole.role !== "COMMENTER" ||
                            reply.author.id === user.userId) && (
                            <>
                              <img
                                src={dotsIcon}
                                alt="menu"
                                className="menu-icon"
                                onClick={(e) => handleMenuClick(e, reply.id)}
                              />
                              <CommentContextMenu
                                isReply={true}
                                comment={reply}
                                commentsFilter={commentsFilter}
                                isOpen={activeMenuId === reply.id}
                                onClose={() => setActiveMenuId(null)}
                                onArchive={() => onArchive(reply.id)}
                                onRestore={() => onRestore(reply.id)}
                                onDelete={() => handleDelete(reply.id)}
                                onForceDelete={() =>
                                  handleForceDelete(reply.id)
                                }
                                isArchived={commentsFilter === "archived"}
                                position={{ top: "100%", right: 0 }}
                                onEdit={() => handleEditComment(reply)}
                                contextMenuPosition={contextMenuPosition}
                              />
                            </>
                          )) || <div style={{ width: 5, height: 5 }} />}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )) ||
              null}
            {(openReplyId === comment.id && (
              <div className="reply-input-row">
                <input
                  className="reply-input"
                  value={replyInputs[comment.id] || ""}
                  onChange={(e) =>
                    setReplyInputs({
                      ...replyInputs,
                      [comment.id]: e.target.value,
                    })
                  }
                  autoFocus={true}
                  placeholder="Напишите комментарий"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAddReply(comment.id);
                  }}
                />
                <button
                  className="reply-send-btn"
                  onClick={() => handleAddReply(comment.id)}
                >
                  <span role="img" aria-label="send">
                    ➤
                  </span>
                </button>
              </div>
            )) ||
              null}
          </div>
        </div>
      ))}
      {activeMarker && <VideoMarker point={activeMarker} isActive={true} />}
      <UserPopup
        user={hoveredUser}
        isVisible={!!hoveredUser}
        position={popupPosition}
      />
    </div>
  );
};

export default CommentsBlock;
